import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Instagram, Facebook, Globe, ArrowUpRight } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative bg-[#0B0F0E] border-t border-[#D4AF37]/20" data-testid="footer">

      {/* MASSIVE wordmark */}
      <div className="overflow-hidden border-b border-[#D4AF37]/15 py-12 md:py-20">
        <h2 className="font-serif text-[20vw] md:text-[14vw] leading-[0.85] text-center text-[#E8E3D9]/8 font-light tracking-tight select-none">
          Tuskermark
        </h2>
      </div>

      <div className="max-w-[1480px] mx-auto px-6 md:px-12 py-16 md:py-20">

        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-10">
          {/* brand */}
          <div className="md:col-span-4">
            <div className="mb-6">
              <img
                src="https://customer-assets.emergentagent.com/job_b94b95c1-d586-44dc-829b-05989d33f0ec/artifacts/jj6t2qbc_tusker-off-whiteAsset.png"
                alt="Tuskermark Safari Retreat"
                className="h-12 w-auto object-contain"
              />
              <div className="overline text-[8px] mt-3" style={{ color: '#D4AF37' }}>Safari Retreat</div>
            </div>
            <p className="text-[#8E9A95] text-[13px] leading-[1.85] mb-8 max-w-xs">
              A nine-room safari resort inside Tholpetty Wildlife Sanctuary, Kerala —
              where the forest is the address.
            </p>
            <div className="flex gap-3">
              {[
                { Icon: Instagram, href: 'https://www.instagram.com/tuskermark_safari_retreat', test: 'footer-instagram' },
                { Icon: Facebook, href: 'https://www.facebook.com/tuskermark', test: 'footer-facebook' },
                { Icon: Globe, href: 'https://www.tuskermark.com', test: 'footer-website' },
              ].map(({ Icon, href, test }) => (
                <a
                  key={test}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 border border-[#D4AF37]/30 text-[#D4AF37] flex items-center justify-center hover:bg-[#D4AF37] hover:text-[#0B0F0E] transition-all"
                  data-testid={test}
                  aria-label="social"
                >
                  <Icon size={14} />
                </a>
              ))}
            </div>
          </div>

          {/* navigate */}
          <div className="md:col-span-2">
            <div className="overline mb-6">Navigate</div>
            <nav className="flex flex-col gap-3 text-[13px]">
              {[
                { l: 'The Sanctuary', h: '/#about' },
                { l: 'Rooms', h: '/#rooms' },
                { l: "Tracker's Journal", h: '/#activities' },
                { l: 'Around', h: '/#sightseeing' },
                { l: 'Blog', h: '/blog' },
                { l: 'Contact', h: '/#contact' },
              ].map((l) => (
                <Link key={l.l} to={l.h} className="link-underline text-[#C4BFB1] hover:text-[#D4AF37] transition-colors w-fit">
                  {l.l}
                </Link>
              ))}
            </nav>
          </div>

          {/* stay */}
          <div className="md:col-span-3">
            <div className="overline mb-6">Stay</div>
            <ul className="text-[13px] text-[#C4BFB1] space-y-3">
              <li className="flex justify-between"><span>Deluxe Rooms</span><span className="numeral text-[#D4AF37]">04</span></li>
              <li className="flex justify-between"><span>Standard Rooms</span><span className="numeral text-[#D4AF37]">03</span></li>
              <li className="flex justify-between"><span>Couple Rooms</span><span className="numeral text-[#D4AF37]">02</span></li>
              <li className="pt-3 border-t border-[#D4AF37]/15 mt-3 flex justify-between">
                <span>Couple · night</span>
                <span className="numeral text-[#D4AF37]">₹5,500</span>
              </li>
              <li className="flex justify-between">
                <span>Standard · night</span>
                <span className="numeral text-[#D4AF37]">₹6,500</span>
              </li>
              <li className="flex justify-between">
                <span>Deluxe · night</span>
                <span className="numeral text-[#D4AF37]">₹7,500</span>
              </li>
            </ul>
          </div>

          {/* contact */}
          <div className="md:col-span-3">
            <div className="overline mb-6">Reach us</div>
            <div className="space-y-4 text-[13px]">
              <a href="tel:+918310838799" className="flex items-center gap-3 text-[#C4BFB1] hover:text-[#D4AF37] transition-colors" data-testid="footer-phone">
                <Phone size={14} className="text-[#D4AF37]" />
                +91 83108 38799
              </a>
              <a href="tel:+918618285170" className="flex items-center gap-3 text-[#C4BFB1] hover:text-[#D4AF37] transition-colors" data-testid="footer-phone-2">
                <Phone size={14} className="text-[#D4AF37]" />
                +91 86182 85170
              </a>
              <a href="mailto:info@tuskermark.com" className="flex items-center gap-3 text-[#C4BFB1] hover:text-[#D4AF37] transition-colors break-all" data-testid="footer-email">
                <Mail size={14} className="text-[#D4AF37] flex-shrink-0" />
                info@tuskermark.com
              </a>
              <div className="flex items-start gap-3 text-[#8E9A95] text-[12px] leading-relaxed pt-2">
                <MapPin size={14} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <span>Tholpetty Wildlife Sanctuary<br/>Kutta–Mananthavady Road<br/>Tholpetty, Kerala — 670646</span>
              </div>
            </div>
          </div>
        </div>

        <div className="brass-rule w-full mt-16" />

        <div className="mt-8 flex flex-col md:flex-row items-center justify-between gap-4 text-[11px] tracking-[0.15em] uppercase">
          <p className="text-[#8E9A95]">© {currentYear} Tuskermark Safari Retreat · All rights reserved</p>
          <a href="#" className="text-[#8E9A95] hover:text-[#D4AF37] transition-colors inline-flex items-center gap-2">
            Back to top <ArrowUpRight size={12} />
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
